import { useState, useEffect } from 'react'
import { useSelector, useStore } from 'react-redux'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, InputAdornment, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { newGetList } from '../../../helper/baseFetch_v2'
import { chipRender } from '../constants'
import Iconify from '../../../Component/common/iconify/Iconify'

const ALLOWED_STATUSES = ['PILOT', 'STABLE']

function getStatusColor(status) {
  const map = { STABLE: 'success', PILOT: 'warning', BETA: 'info', DEPRECATED: 'error' }
  return map[status] ?? 'default'
}

// camelCase converter: 'ANDROID_MODERN' → 'androidModern'
function toCamelKey(str) {
  return str.toLowerCase().replace(/_([a-z])/g, (_, c) => c.toUpperCase())
}

function BuildTargetChips({ buildTargetMap }) {
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap">
      {Object.entries(buildTargetMap ?? {})
        .filter(([, v]) => v)
        .map(([key]) => (
          <Chip
            key={key}
            size="small"
            variant="outlined"
            label={key.replace(/([A-Z])/g, ' $1').trim()}
            icon={<Iconify icon={chipRender(key)?.icon} />}
            color={chipRender(key)?.color ?? 'default'}
          />
        ))}
    </Stack>
  )
}

// ── Step 2: Release picker dialog ──────────────────────────────────────────
export default function ReleasePickerDialog({ open, onClose, versionGroup, buildTarget, onSelect }) {
  const allowedKey = buildTarget ? toCamelKey(buildTarget) : null

  const releases = (versionGroup?.releaseList ?? []).filter((r) => {
    const statusOk = ALLOWED_STATUSES.includes(r.status)
    const buildTargetOk = allowedKey ? r.buildTarget?.[allowedKey] === true : true
    return statusOk && buildTargetOk
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Select Release — <Typography component="span" color="primary.main">v{versionGroup?.appVersion} {versionGroup?.customCode && `(${versionGroup?.customCode})`}</Typography>
          </Typography>
          {buildTarget && (
            <Chip
              size="small"
              label={buildTarget.replace(/_/g, ' ')}
              icon={<Iconify icon={chipRender(buildTarget)?.icon} />}
              color={chipRender(buildTarget)?.color ?? 'primary'}
              variant="outlined"
            />
          )}
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {releases.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              No {ALLOWED_STATUSES.join(' / ')} releases available
              {buildTarget ? ` for ${buildTarget}` : ''}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Build No.</TableCell>
                  <TableCell>Custom Code</TableCell>
                  <TableCell>Build Targets</TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {releases.map((r) => (
                  <TableRow
                    key={r.id}
                    hover
                    onClick={() => onSelect(r)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{r.buildNumber}</Typography>
                    </TableCell>
                    <TableCell>{r.customCode ?? '-'}</TableCell>
                    <TableCell><BuildTargetChips buildTargetMap={r.buildTarget} /></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {(r.tagList ?? []).map((tag) => (
                          <Chip key={tag} label={tag} size="small" />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={r.status} color={getStatusColor(r.status)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  )
}